import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { analyzeVideo } from "../../analysis/pipeline";
import { WebcamRecorder } from "../components/WebcamRecorder";
import { GaitSilhouette } from "../components/GaitSilhouette";
import { loadVideoFromBlob } from "../../capture/video";
import { PILOT } from "../../config";
import {
  saveSession,
  saveStream,
  saveAnalysis,
  saveVideoBlob,
  savePatient,
  getPatient,
  listSessions,
  uuid,
} from "../../store/db";
import {
  CURRENT_VERSIONS,
  type GaitView,
  type Milestone,
  type Session,
} from "../../store/types";

const MILESTONES: { value: Milestone; label: string }[] = [
  { value: "adhoc", label: "Einzelmessung" },
  { value: "baseline", label: "Erstmessung / Baseline" },
  { value: "6w", label: "Verlauf · 6 Wochen" },
  { value: "3m", label: "Verlauf · 3 Monate" },
  { value: "6m", label: "Verlauf · 6 Monate" },
  { value: "12m", label: "Verlauf · 12 Monate" },
];

export function CaptureScreen() {
  const nav = useNavigate();
  const [view, setView] = useState<GaitView>("lateral");
  const [code, setCode] = useState("");
  const [milestone, setMilestone] = useState<Milestone>("adhoc");
  const [heightCm, setHeightCm] = useState("");
  const [sex, setSex] = useState<"" | "F" | "M" | "X">("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [recent, setRecent] = useState<Session[]>([]);
  const [showRecorder, setShowRecorder] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    listSessions().then(setRecent);
  }, [busy]);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await runOn(file);
    if (fileRef.current) fileRef.current.value = "";
  }

  async function onRecorded(blob: Blob) {
    setShowRecorder(false);
    await runOn(blob);
  }

  async function runOn(blob: Blob) {
    setError(null);
    setBusy(true);
    setProgress(0);
    try {
      const sessionId = await processFile(blob);
      nav(`/review/${sessionId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function processFile(file: Blob): Promise<string> {
    const { video, url } = await loadVideoFromBlob(file);
    const aspect =
      video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : 16 / 9;
    const h = parseFloat(heightCm);

    const out = await analyzeVideo(video, {
      view,
      aspect,
      heightCm: isFinite(h) && h > 0 ? h : undefined,
      variant: "full",
      onProgress: setProgress,
    });
    URL.revokeObjectURL(url);

    // Persist patient (by code), session, stream, analysis, video blob.
    const patientId = await ensurePatient(code.trim() || "Unlabeled");
    const sessionId = uuid();
    const session: Session = {
      id: sessionId,
      patientId,
      view,
      milestone,
      recordedAt: Date.now(),
      videoRef: `idb:video:${sessionId}`,
      fps: out.fps,
      frameCount: out.frames.length,
      durationMs: out.durationMs,
      qc: {
        passed: out.qc.passed,
        fullBodyRatio: out.qc.fullBodyRatio,
        meanConfidence: out.qc.meanConfidence,
        fps: out.qc.fps,
        detectedCycles: out.qc.detectedCycles,
        warnings: out.qc.warnings,
      },
      versions: CURRENT_VERSIONS,
      createdAt: Date.now(),
    };

    await saveVideoBlob(sessionId, file);
    await saveStream({ sessionId, schemaVersion: 1, frames: out.frames });
    await saveAnalysis({ sessionId, ...out.analysis });
    await saveSession(session);
    return sessionId;
  }

  return (
    <>
      <div className="card hero">
        <GaitSilhouette />
        <h2>Gang verstehen. In einem Schritt.</h2>
        <p>
          Ganganalyse für alle — ein Video genügt. Pose, Phasen, Symmetrie, ROM
          &amp; Score, vollständig auf diesem Gerät.
        </p>
      </div>

      <div className="card">
        <strong>Neue Analyse</strong>

        <label>Person — Name oder ID</label>
        <input
          type="text"
          placeholder="z. B. Anna M. oder HTO-014"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={busy}
        />

        <label>Ansicht</label>
        <div className="segmented">
          <button
            className={view === "lateral" ? "active" : ""}
            onClick={() => setView("lateral")}
            disabled={busy}
          >
            Seitlich
          </button>
          <button
            className={view === "frontal" ? "active" : ""}
            onClick={() => setView("frontal")}
            disabled={busy}
          >
            Frontal
          </button>
        </div>

        <button
          type="button"
          className="disclosure"
          aria-expanded={showDetails}
          onClick={() => setShowDetails((v) => !v)}
          disabled={busy}
        >
          <span className={`chev ${showDetails ? "open" : ""}`}>›</span>
          Optionale Angaben
          <span className="muted" style={{ fontWeight: 400 }}>
            Größe, Geschlecht, Anlass
          </span>
        </button>

        {showDetails && (
          <div className="disclosure-body">
            <div className="row" style={{ gap: 10, alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <label>Größe (cm)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="z. B. 175 — für Tempo & Schrittlänge"
                  value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div style={{ width: 96 }}>
                <label>Geschlecht</label>
                <select value={sex} onChange={(e) => setSex(e.target.value as "" | "F" | "M" | "X")} disabled={busy}>
                  <option value="">—</option>
                  <option value="F">F</option>
                  <option value="M">M</option>
                  <option value="X">X</option>
                </select>
              </div>
            </div>
            <label>Anlass</label>
            <select
              value={milestone}
              onChange={(e) => setMilestone(e.target.value as Milestone)}
              disabled={busy}
            >
              {MILESTONES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {busy ? (
          <div style={{ marginTop: 18 }}>
            <div className="progress">
              <div style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>
            <p className="muted">Analyse auf dem Gerät… {Math.round(progress * 100)}%</p>
          </div>
        ) : (
          <>
            <label className="consent" style={{ marginTop: 18 }}>
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              Einwilligung der Person zur Aufnahme liegt vor
            </label>
            <input
              ref={fileRef}
              type="file"
              accept="video/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={onFile}
            />
            <button
              className="btn"
              style={{ marginTop: 12 }}
              disabled={!consentChecked}
              onClick={() => fileRef.current?.click()}
            >
              Video aufnehmen / importieren
            </button>
            <button
              className="btn secondary"
              style={{ marginTop: 10 }}
              disabled={!consentChecked}
              onClick={() => setShowRecorder(true)}
            >
              Capture
            </button>
            <p className="muted" style={{ marginTop: 10 }}>
              Auf dem iPhone öffnet der erste Button direkt die Kamera. Am Mac eine
              Datei wählen oder „Capture" nutzen.
            </p>
          </>
        )}
        {error && <p style={{ color: "var(--danger)" }}>{error}</p>}
      </div>

      {showRecorder && !busy && (
        <WebcamRecorder view={view} onRecorded={onRecorded} onCancel={() => setShowRecorder(false)} />
      )}

      <div className="card">
        <button
          type="button"
          className="disclosure"
          aria-expanded={showTips}
          onClick={() => setShowTips((v) => !v)}
        >
          <span className={`chev ${showTips ? "open" : ""}`}>›</span>
          Tipps für eine gute Aufnahme
        </button>
        {showTips && (
          <ul className="checklist">
            <li>Kamera fixiert (Stativ oder angelehnt)</li>
            <li>Ganzer Körper das ganze Video sichtbar</li>
            <li>5–10 m Gehstrecke, natürliches Tempo</li>
            <li>Gleichmäßiges Licht · kurze Hose bevorzugt</li>
            <li>{view === "frontal" ? "Auf die Kamera zu / von ihr weg gehen" : "Seitlich durchs Bild gehen"}</li>
          </ul>
        )}
      </div>

      {recent.length > 0 && (
        <div className="card">
          <strong>Zuletzt analysiert</strong>
          {recent.slice(0, 8).map((s) => (
            <div className="session-item" key={s.id}>
              <span className="muted">
                {new Date(s.recordedAt).toLocaleDateString()} ·{" "}
                {s.view === "frontal" ? "frontal" : "seitlich"}
              </span>
              <span className="row" style={{ gap: 14 }}>
                <a href={`#/trend/${s.patientId}`} className="muted" style={{ fontWeight: 600 }}>
                  Verlauf
                </a>
                <a href={`#/review/${s.id}`}>öffnen</a>
              </span>
            </div>
          ))}
        </div>
      )}

      <p className="disclaimer">
        CADtomie Gait ist ein funktionelles Screening- und Verlaufs-Werkzeug —
        kein Ersatz für ein Ganglabor und kein Diagnosegerät. Beta · Scores nutzen
        unkalibrierte Seed-Schwellen. Alle Videos und Daten bleiben auf diesem Gerät.
        <br />
        <a href={`mailto:${PILOT.feedbackEmail}?subject=CADtomie%20Gait%20Feedback`}>
          Feedback senden
        </a>
      </p>
    </>
  );

  async function ensurePatient(codeLabel: string): Promise<string> {
    const id = "pt-" + codeLabel.toLowerCase().replace(/\s+/g, "-");
    const existing = await getPatient(id);
    const now = Date.now();
    const h = parseFloat(heightCm);
    const demo = {
      heightCm: isFinite(h) && h > 0 ? h : undefined,
      sex: sex || undefined,
    };
    if (existing) {
      await savePatient({ ...existing, ...demo, updatedAt: now });
      return id;
    }
    await savePatient({ id, code: codeLabel, ...demo, createdAt: now, updatedAt: now });
    return id;
  }
}
